/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useState } from 'react';
import { Button, Icon, FileDrop } from '@scuf/common';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import { useFileUploadContext } from '../../../../context/FileUploadContext/FileUploadContext';
import FileUploadAction from '../../../../context/FileUploadContext/FileUploadActions';
import {
  fileUploadSizeDecimal,
  MAGIC_VALUE,
  ZLOAssociationUploadSettings
} from '../../../shared/Utils/Constants';
import _ from 'lodash';
import { ApiContext } from '../../../../context/ApiContext';

const fileNameTruncate = 36;
interface IFile extends File {
  preview: string;
}

const InfoIcon = () => <Icon size="small" name="badge-info" root="common" />;

interface IZLOUploadTabProps {
  fileValidationMessages: string[];
}

const UploadTab: React.FC<IZLOUploadTabProps> = (props) => {
  const d = descriptors.energyManagement;
  const z = descriptors.ZLOOnboardingConfig;
  const [t] = useTranslation();

  const apiContext = useContext(ApiContext);
  const { onNewTemplateDownloadClick } = apiContext;

  const [state, dispatch] = useFileUploadContext();
  let zloFile: IFile | null = null;
  zloFile = state.zloFile || null;

  const { fileValidationMessages } = props;

  const setZloFile = (file: File | null) =>
    dispatch(FileUploadAction.setZLOFile(file));

  const removeFile = () => {
    setZloFile(null);
  };

  const [notAcceptedFileName, setNotAcceptedFileName] = useState('');

  return (
    <div className="upload-tab-wrapper">
      <div className="top-upload-note">{t(z.UPLOAD_TAB_TOP_NOTE)}</div>
      <div className="download-template-btn-wrapper">
        <Button
          className="download-template-button"
          icon={'file-download'}
          type="link"
          onClick={() => {
            if (onNewTemplateDownloadClick) {
              onNewTemplateDownloadClick(
                'ZLO',
                ZLOAssociationUploadSettings.downloadFile || '',
                ZLOAssociationUploadSettings.uploadType || ''
              );
            }
          }}
          content={t(z.UPLOAD_TAB_DOWNLOAD_TEMPLATE_BTN)}
        />
      </div>
      <div className="upload-tab-content-wrapper">
        <div className={'upload-modal-filedrop'}>
          <FileDrop
            disabled={!_.isEmpty(zloFile)}
            className={`${!_.isEmpty(zloFile) ? 'disabled' : ''}`}
            multiple={false}
            label=""
            acceptedTypes={[
              ...ZLOAssociationUploadSettings.extensions,
              ...ZLOAssociationUploadSettings.mimeTypes
            ]}
            placeholder={t(z.UPLOAD_TAB_PLACEHOLDER)}
            error={
              !_.isEmpty(zloFile)
                ? t(z.UPLOAD_TAB_SECOND_NOTE)
                : t(d.UPLOADNOTE, {
                    type: _.join(ZLOAssociationUploadSettings.extensions, ', '),
                    size: _.toInteger(
                      ZLOAssociationUploadSettings.maxSize /
                        MAGIC_VALUE.MEGABYTES
                    )
                  })
            }
            onAccepted={(files) => {
              setNotAcceptedFileName('');
              setZloFile(files[0]);
            }}
            onRejected={(files) => {
              setNotAcceptedFileName(files[0].name);
            }}
          ></FileDrop>
        </div>
        {zloFile ? (
          <>
            <div className="file-names">
              <div className="file-uploaded">
                <div className="full-width uploaded-file-details">
                  <div className="w-6">
                    <Icon root="common" name="document-report" size="small" />
                  </div>
                  <div className="w-79 align-left">
                    <div>
                      {`${_.truncate(zloFile.name, {
                        length: fileNameTruncate
                      })} (${_.toNumber(
                        zloFile.size / MAGIC_VALUE.MEGABYTES
                      ).toFixed(fileUploadSizeDecimal)} MB)`}
                    </div>
                    <div className="validation-messages">
                      {_.map(fileValidationMessages, (m: string) => (
                        <div className="file-validation-message-wrapper">
                          <span>{m}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="remove-button w-15 pl-5">
                    <Button
                      type="link"
                      content={t(d.REMOVE)}
                      onClick={() => removeFile()}
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          notAcceptedFileName && (
            <div className="validation-messages">
              <div className="file-validation-message-wrapper">
                <span>
                  {t(descriptors.equipmentSpaceMapping.FILE_NOT_ACCEPTED, {
                    fileName: notAcceptedFileName,
                    extension: _.join(
                      ZLOAssociationUploadSettings.extensions,
                      ' / '
                    )
                  })}
                </span>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default UploadTab;
